import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../../common/types';
import { FieldTooltipContent } from '../../../../../../common/ui/FieldTooltipContent';
import { formatSuggestedDateTime } from '../../../../../../common/utils/date-formatter-utils';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../../common/utils/useRegisterTooltipContentProvider';
import {
	formatSecondsToHuman,
	formatSecondsWithSettings,
	parseSuggestionToSeconds,
} from '../utils';

import type { timeSpentField_updateWorkItemFieldsActionRenderer$key } from './__generated__/timeSpentField_updateWorkItemFieldsActionRenderer.graphql';
import { toTimeSpentFieldValue } from './utils';

const messages = defineMessages({
	timeSpentLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.time-spent-field.time-spent-label',
		defaultMessage: 'Current time spent',
		description: 'Label for the time spent in the time spent field',
	},
	timeSpentSuggestedLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.time-spent-field.time-spent-suggested-label',
		defaultMessage: 'Suggested time spent',
		description: 'Label for the suggested time spent in the time spent field',
	},
	startDateSuggestedLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.time-spent-field.start-date-suggested-label',
		defaultMessage: 'Suggested start date',
		description: 'Label for the suggested start date in the time spent field',
	},
	commentSuggestedLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.time-spent-field.comment-suggested-label',
		defaultMessage: 'Suggested comment',
		description: 'Label for the suggested comment in the time spent field',
	},
	set: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.time-spent-field.set-label',
		defaultMessage: 'Set time spent to {timeSpent}',
		description: 'Label for the set button in the time spent field',
	},
});

export type Props = {
	queryRef: timeSpentField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const TimeSpentField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage, formatDate } = useIntl();

	const data = useFragment(
		graphql`
			fragment timeSpentField_updateWorkItemFieldsActionRenderer on JiraTimeTrackingField {
				fieldId
				name
				timeTrackingSettings {
					isJiraConfiguredTimeTrackingEnabled
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
				timeSpent {
					timeInSeconds
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable =
		data?.fieldId === 'timetracking' && action?.data?.fieldKey === 'timespent';
	const isTimeTrackingEnabled = !!data?.timeTrackingSettings?.isJiraConfiguredTimeTrackingEnabled;

	const fieldValue = toTimeSpentFieldValue(action?.data?.fieldValue);
	const suggestedTimeSpent = fieldValue?.timeSpent;
	const suggestedStarted = fieldValue?.started;
	const suggestedComment = fieldValue?.comment;

	// There cannot be a case where we "clear" the timespent - there always must be a value.
	const shouldRender = isFieldRenderable && isTimeTrackingEnabled && !!suggestedTimeSpent;

	const { defaultUnit, defaultFormat, workingHoursPerDay, workingDaysPerWeek } =
		data?.timeTrackingSettings ?? {};

	const currentTimeSpent = formatSecondsToHuman(data?.timeSpent?.timeInSeconds);

	const normalizedSuggestedSeconds = parseSuggestionToSeconds(suggestedTimeSpent || '', {
		defaultUnit,
		defaultFormat,
		workingHoursPerDay,
		workingDaysPerWeek,
	});

	const normalizedOriginalValue = useMemo(
		() =>
			normalizedSuggestedSeconds != null
				? formatSecondsWithSettings(normalizedSuggestedSeconds, {
						defaultUnit,
						defaultFormat,
						workingHoursPerDay,
						workingDaysPerWeek,
					})
				: suggestedTimeSpent,
		[
			normalizedSuggestedSeconds,
			defaultUnit,
			defaultFormat,
			workingHoursPerDay,
			workingDaysPerWeek,
			suggestedTimeSpent,
		],
	);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				current={currentTimeSpent}
				currentLabel={formatMessage(messages.timeSpentLabel)}
				suggested={normalizedOriginalValue}
				suggestedLabel={formatMessage(messages.timeSpentSuggestedLabel)}
				additionalSuggestions={[
					{
						suggested: formatSuggestedDateTime(suggestedStarted, formatDate),
						label: formatMessage(messages.startDateSuggestedLabel),
						testIdSuffix: 'time-spent-field-start-date-suggestion',
					},
					{
						suggested: suggestedComment,
						label: formatMessage(messages.commentSuggestedLabel),
						testIdSuffix: 'time-spent-field-comment-suggestion',
					},
				]}
			/>
		),
		[
			action?.data?.reasoning,
			data?.name,
			currentTimeSpent,
			normalizedOriginalValue,
			suggestedStarted,
			suggestedComment,
			formatMessage,
			formatDate,
		],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: shouldRender,
	});

	if (!shouldRender) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.time-spent-field"
		>
			{formatMessage(messages.set, { timeSpent: normalizedOriginalValue })}
		</Text>
	);
};
